import { FiClock, FiCheck, FiX } from "react-icons/fi";
import { formatIndianAmount, getFormattedDate } from "../../../Utils";
import { MdEdit } from "react-icons/md";
import { IoMdAddCircle } from "react-icons/io";
import { CommonForm, RoleValidator, Spinner } from "../../../Components/Common";
import { ROLES } from "../../../Constants/Strings";
import { FaChevronDown, FaFolderOpen } from "react-icons/fa";
import { useEffect, useState } from "react";
import EditPopUp from "./EditPopUp";
import PaymentFiles from "./PaymentFiles";
import {
  addProjectPayment,
  getPaymentTimeline,
  updatePaymentById,
} from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../constants";
import { useParams } from "react-router-dom";

const statusStyles = {
  PENDING: {
    label: "Pending",
    bg: "bg-yellow-100",
    text: "text-yellow-600",
    dot: "bg-yellow-500",
    icon: <FiClock size={14} />,
  },
  COMPLETED: {
    label: "Completed",
    bg: "bg-green-100",
    text: "text-green-600",
    dot: "bg-green-500",
    icon: <FiCheck size={14} />,
  },
  FAILED: {
    label: "Declined",
    bg: "bg-red-100",
    text: "text-red-600",
    dot: "bg-red-500",
    icon: <FiX size={14} />,
  },
};

const PaymentCard = ({ currentMilestoneStatus }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPaymentFiles, setShowPaymentFiles] = useState(false);
  const [addPaymentPopUp, showAddPaymentPopUp] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const { projectId } = useParams();
  const [paymentFormData, setPaymentFormData] = useState({
    amount: "",
    payment_date: "",
    phase_description: "",
    payment_status: "",
  });

  const payment_formFiledLabels = {
    amount: "Amount",
    payment_date: "Date",
    payment_status: "Status",
    phase_description: "Phase Description",
  };

  const payment_renderFiledConfig = {
    amount: { type: "TEXT" },
    payment_date: { type: "DATE" },
    payment_status: {
      type: "SELECT",
      dropdownValues: ["PENDING", "COMPLETED"],
    },
    phase_description: { type: "TEXT" },
  };

  const fetchPaymentDetails = () => {
    setLoading(true);
    getPaymentTimeline(projectId)
      .then((response) => {
        if (response.status === 200) {
          setPaymentDetails(response.data);
        } else {
          toast.error(TOAST_MESSAGES.PAYMENT_FETCH_FAILED);
        }
      })
      .catch(() => {
        toast.error(TOAST_MESSAGES.PAYMENT_FETCH_FAILED);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addPayment = async () => {
    try {
      setIsSubmitting(true);
      const response = await addProjectPayment(projectId, paymentFormData);

      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.PAYMENT_ADD_SUCCESS);
        fetchPaymentDetails();
        showAddPaymentPopUp(false);
      } else {
        toast.error(TOAST_MESSAGES.PAYMENT_ADD_FAILED);
      }
    } catch {
      toast.error(TOAST_MESSAGES.PAYMENT_ADD_FAILED);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavePayment = async (updatedData) => {
    try {
      await updatePaymentById(projectId, paymentId, updatedData);
      toast.success(TOAST_MESSAGES.PAYMENT_UPDATE_SUCCESS);
      setPaymentFormData((prev) => ({ ...prev, ...updatedData }));
      fetchPaymentDetails();
      setShowPaymentPopup(false);
    } catch (error) {
      toast.error(TOAST_MESSAGES.PAYMENT_UPDATE_FAILED);
    }
  };

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  return (
    <div className="bg-white shadow-sm p-2 rounded-md">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold mb-4 px-2">Payment TimeLines</h2>
        <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
          <div className="flex gap-1">
            <button
              className="flex gap-2 p-1 text-primary px-4 items-center"
              onClick={() => showAddPaymentPopUp(true)}
              title="Add Payment"
            >
              <IoMdAddCircle size={20} />
            </button>
            <button
              className="flex gap-2 p-1 items-center text-yellow-500"
              onClick={() => setShowPaymentFiles(true)}
              title="Upload File"
            >
              <FaFolderOpen size={20} />
            </button>
          </div>
        </RoleValidator>
      </div>
      <div className="flex gap-2 items-center py-4">
        <div className="px-2 flex font-bold text-lg">
          {formatIndianAmount(paymentDetails?.total_received, 0)}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          {paymentDetails.percent_received > 0 && (
            <div
              className="bg-[#219ebc] h-4 text-xs font-semibold text-white text-center transition-all duration-500"
              style={{ width: `${paymentDetails?.percent_received}%` }}
            >
              {paymentDetails?.percent_received}%
            </div>
          )}
        </div>

        <div
          className="flex flex-col justify-end p-2"
          onClick={() => setShowDetails(!showDetails)}
        >
          <FaChevronDown
            className={`text-gray-500 transition-transform duration-300  ${
              showDetails ? "rotate-180" : ""
            }`}
            size={16}
          />
        </div>
      </div>
      {showDetails && (
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200"></div>

          {paymentDetails.payments.map((item, index) => {
            const status =
              statusStyles[item.payment_status] || statusStyles.PENDING;

            return (
              <div key={item.id} className="relative pl-10 mb-6">
                {/* Status Dot */}
                <span
                  className={`absolute left-1.5 top-2 w-3 h-3 rounded-full ${status.dot}`}
                ></span>

                {/* Card */}
                <div className="flex justify-between bg-white border rounded-lg p-2">
                  <div className="flex flex-col justify-between">
                    <p className="text-lg font-semibold">
                      ₹ {parseFloat(item.amount).toLocaleString()}
                    </p>
                    <div className="md:flex  items-center gap-2">
                      <p className="text-sm text-gray-500">
                        {getFormattedDate(
                          new Date(item.payment_date),
                          "dd/MM/yyyy"
                        )}
                      </p>
                      <div className="text-gray-400 md:block hidden">|</div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-700">
                          {item.phase_description}
                        </p>
                        <p className="text-xs text-gray-400">
                          Created by {item.created_by}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${status.bg} ${status.text}`}
                    >
                      {status.icon}
                      {status.label}
                    </span>
                    <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
                      <button className="flex gap-2 p-1 text-primary px-4 item-center">
                        <MdEdit
                          size={20}
                          onClick={() => {
                            setShowPaymentPopup(true);
                            setPaymentId(item.id);
                            setPaymentFormData({
                              amount: item.amount,
                              payment_date: item.payment_date,
                              phase_description: item.phase_description,
                              payment_status: item.payment_status,
                            });
                          }}
                          title="Edit Payments"
                        />
                      </button>
                    </RoleValidator>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showPaymentPopup && (
        <EditPopUp
          onClose={() => setShowPaymentPopup(false)}
          onSave={handleSavePayment}
          formData={paymentFormData}
          renderFieldConfig={payment_renderFiledConfig}
          formLabelFields={payment_formFiledLabels}
          setFormData={setPaymentFormData}
        />
      )}

      {showPaymentFiles && (
        <PaymentFiles
          onClose={() => setShowPaymentFiles(false)}
          currentMilestoneStatus={currentMilestoneStatus}
        />
      )}

      {addPaymentPopUp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 m-4 w-full max-w-lg relative">
            <h2 className="text-lg font-semibold mb-4">Add Payment</h2>

            <CommonForm
              formData={paymentFormData}
              handleChangeData={(field, value) =>
                setPaymentFormData((prev) => ({ ...prev, [field]: value }))
              }
              renderField={payment_renderFiledConfig}
              formFieldLabels={payment_formFiledLabels}
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => showAddPaymentPopUp(false)}
                className="bg-gray-300 px-4 py-2 rounded disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={addPayment}
                className="bg-primary hover:bg-secondary text-white px-4 py-2 rounded disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCard;
